import { Injectable } from '@angular/core';
import { confirmNegative, ModalMessageInfo } from '@app/core/components/modal';
import { UserService } from '@app/core/services/user.service';
import { baseRemovalModalMessageInfo } from '@app/core/utils/base-modal-info';
import { SetFilter } from '@app/pcs/actions/advanced-search';
import { SearchDossiers } from '@app/pcs/actions/dossier-search';
import * as FilterActions from '@app/pcs/actions/filter';
import {
  ConfirmFilterRemoval,
  CreateFilter,
  CreateFilterFail,
  DeleteFilter,
  DeleteFilterFail,
  DeleteFilterSuccess,
  FetchFilterSearchCriteriaFail,
  FetchFilterSearchCriteriaSuccess,
  FilterActionTypes,
  SelectFilter,
  UpdateFilter,
  UpdateFilterFail
} from '@app/pcs/actions/filter';
import { SaveUserFilterSuccess } from '@app/pcs/actions/user-filter';
import { DossierSearchCriteria, Filter, UserFilter } from '@app/pcs/models';
import * as fromPcs from '@app/pcs/reducer';
import * as fromAdvancedSearch from '@app/pcs/reducer/advanced-search';
import { UserFilterDataService } from '@app/pcs/services/user-filter-data.service';
import { prepareAdvancedSearchForm } from '@app/pcs/shared/components/advanced-search/advanced-search-util';
import { ResetUserDefaultFilter } from '@app/pcs/user/actions/user';
import { getFilterId } from '@app/pcs/util/user-filter';
import { createValidationErrorObject } from '@app/shared/validators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { defaultDebounceTime, hasValue, mapPayload } from '@util';
import { toLocalDateTimeArrayFromLocalDateArray } from '@util/date-util';
import { ClearAsyncErrorAction, SetAsyncErrorAction, StartAsyncValidationAction } from 'ngrx-forms';
import * as R from 'ramda';
import { of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter as filterOp,
  map,
  startWith,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';

const filterNameExistsErrorKey = 'filterNameExists';

const setErrorAction = (controlId: string, filterName: string) => of(
  new SetAsyncErrorAction(
    controlId,
    filterNameExistsErrorKey,
    createValidationErrorObject('dossiersearch.addfilter.duplicateName', [filterName])
  )
);

const clearErrorAction = (controlId: string) => of(new ClearAsyncErrorAction(controlId, filterNameExistsErrorKey));

const removeFilterMsg = (filterName: string): ModalMessageInfo => ({
  ...baseRemovalModalMessageInfo,
  message: 'dashboard.delete.filter.infoMessage',
  messageParams: [filterName]
});

@Injectable()
export class FilterEffects {

  searchCriteria$ = this.store.pipe(
    select(fromAdvancedSearch.getAdvancedSearchForm),
    map(prepareAdvancedSearchForm)
  );

  selectedFilterName$ = this.store.pipe(
    select(fromAdvancedSearch.getFilter),
    map(R.prop('name'))
  );

  validateFilterName$ = createEffect(() => this.actions$.pipe(
    ofType<FilterActions.ValidateFilterName>(FilterActionTypes.ValidateFilterName),
    defaultDebounceTime(),
    mapPayload(),
    distinctUntilChanged<{ controlId: string; value: string }>(R.eqProps('value')),
    withLatestFrom(this.selectedFilterName$),
    filterOp(([{controlId, value}, oldFilterName]) => !R.equals(value, oldFilterName)),
    switchMap(([{controlId, value}]) =>
      this.userFilterDataService.validateFilterName(value).pipe(
        switchMap((filterNameExists: boolean) => filterNameExists ? setErrorAction(controlId, value) : clearErrorAction(controlId)),
        catchError((error: Error) => of(new FilterActions.ValidateFilterNameFail(error))),
        startWith(new StartAsyncValidationAction(controlId, filterNameExistsErrorKey))
      )
    )
  ));

  fetchFilterSearchCriteria$ = createEffect(() => this.actions$.pipe(
    ofType<FilterActions.FetchFilterSearchCriteria>(FilterActionTypes.FetchFilterSearchCriteria),
    mapPayload(),
    exhaustMap((filterId: number) =>
      this.userFilterDataService.fetchFilterSearchCriteria(filterId).pipe(
        switchMap((filter: Filter) => of(new FetchFilterSearchCriteriaSuccess(filter))),
        catchError((error: Error) => of(new FetchFilterSearchCriteriaFail(error)))
      )
    )
  ));

  createFilter$ = createEffect(() => this.actions$.pipe(
    ofType<CreateFilter>(FilterActionTypes.CreateFilter),
    mapPayload(),
    withLatestFrom(this.searchCriteria$),
    exhaustMap(([filter, searchCriteria]) =>
      this.createOrUpdateFilter(filter, searchCriteria).pipe(
        switchMap((userFilter: UserFilter) => [
          new SaveUserFilterSuccess(userFilter),
          new SelectFilter(getFilterId(userFilter))
        ]),
        catchError((error: Error) => of(new CreateFilterFail(error)))
      )
    )
  ));

  updateFilter$ = createEffect(() => this.actions$.pipe(
    ofType<UpdateFilter>(FilterActionTypes.UpdateFilter),
    mapPayload(),
    withLatestFrom(this.searchCriteria$),
    exhaustMap(([filter, dossierSearchModel]) =>
      this.createOrUpdateFilter(filter, dossierSearchModel).pipe(
        switchMap((userFilter: UserFilter) => [
          new SaveUserFilterSuccess(userFilter),
          new SetFilter(userFilter.filter)
        ]),
        catchError((error: Error) => of(new UpdateFilterFail(error)))
      )
    )
  ));

  confirmFilterRemoval$ = createEffect(() => this.actions$.pipe(
    ofType<ConfirmFilterRemoval>(FilterActionTypes.ConfirmFilterRemoval),
    mapPayload(),
    map(({name, id}: Filter) => confirmNegative(removeFilterMsg(name), [new DeleteFilter(id)]))
  ));

  deleteFilter$ = createEffect(() => this.actions$.pipe(
    ofType<DeleteFilter>(FilterActionTypes.DeleteFilter),
    mapPayload(),
    exhaustMap((filterId) =>
      this.userFilterDataService.deleteFilter(filterId).pipe(
        switchMap(() => [
          new DeleteFilterSuccess(filterId),
          new ResetUserDefaultFilter(filterId),
          new SearchDossiers()
        ]),
        catchError((error: Error) => of(new DeleteFilterFail(error)))
      )
    )
  ));

  createOrUpdateFilter = (filter: Filter, searchCriteria: DossierSearchCriteria) =>
    this.userFilterDataService.saveOrUpdate({
      ...filter,
      validFrom: hasValue(filter.validFrom) ? toLocalDateTimeArrayFromLocalDateArray(filter.validFrom) : filter.validFrom,
      validTo: hasValue(filter.validTo) ? toLocalDateTimeArrayFromLocalDateArray(filter.validTo) : filter.validTo,
      dossierSearchModel: R.omit(['trainType'], searchCriteria),
      creatorId: this.userService.getId()
    });


  constructor(private store: Store<fromPcs.State>,
    private actions$: Actions,
    private userFilterDataService: UserFilterDataService,
    private userService: UserService) {
  }

}
